import { Box, Stack, Theme, Typography } from '@mui/material';
import { GQOnboardingKind } from '@watershed/app-dashboard/generated/graphql-schema-types';

import { Trans, useLingui } from '@lingui/react/macro';
import IconButton from '@watershed/ui-core/components/IconButton';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@watershed/icons/components/Close';
import Logo from '@watershed/ui-core/components/Logo';
import Link from '@watershed/ui-core/components/TextLink';
import { useUserContext } from '../../utils/UserContext';
import { useUserOnboardings } from '../../utils/UserOnboardingsContext';
import { SERIF_FONT_FAMILY } from '@watershed/style/styleUtils';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { routeForSupport } from '@watershed/shared-universal/dashboardRoutes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    orgIcon: {
      borderRadius: '50%',
      border: `4px solid ${theme.palette.white}`,
      backgroundColor: theme.palette.white,
      outline: `1px solid ${theme.palette.cobalt10}`,
    },
    rePositionIcons: {
      marginLeft: theme.spacing(-0.5),
    },
  })
);

export default function WelcomeCard() {
  const { t } = useLingui();
  const { orgName, orgHelpChannel, iconUrl: orgIconUrl } = useUserContext();

  const [userOnboardingsCompleted, completeOnboarding] = useUserOnboardings();

  const welcomeOnboardingComplete = userOnboardingsCompleted.has(
    GQOnboardingKind.WelcomeOnboarding
  );

  const displayWelcomeOnboarding = !welcomeOnboardingComplete;

  const classes = useStyles();

  if (displayWelcomeOnboarding) {
    const onClose = () => {
      completeOnboarding(GQOnboardingKind.WelcomeOnboarding);
    };
    // Keep in sync with HomeCardBase styling
    return (
      <Stack
        padding={3}
        bgcolor={(theme) => theme.palette.cobalt05}
        border={(theme) => `1px solid ${theme.palette.cobalt10}`}
        borderRadius={1.5}
        gap={1}
        data-testid={TestIds.HomeWelcomeCard}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            width="100%"
            aria-hidden
          >
            {orgIconUrl && (
              <img
                src={orgIconUrl}
                alt=""
                className={classes.orgIcon}
                style={{
                  marginTop: -16,
                  maxHeight: '24px',
                  maxWidth: '24px',
                }}
              />
            )}
            <Logo
              size={24}
              className={
                orgIconUrl
                  ? `${classes.orgIcon} + ${classes.rePositionIcons}`
                  : ''
              }
            />
            <Typography
              fontFamily={SERIF_FONT_FAMILY}
              fontSize="24px"
              lineHeight="24px"
              fontWeight="500"
            >
              {orgIconUrl ? t`${orgName} x Watershed` : t`About Watershed`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} title={t`Dismiss message`} size="large">
            <CloseIcon size={16} />
          </IconButton>
        </Stack>
        <Stack gap={0.5}>
          <Typography variant="body2">
            <Trans>
              Watershed helps businesses measure, reduce, and report their
              carbon emissions. {orgName} is using it to power its
              sustainability program.
            </Trans>
          </Typography>
          {orgHelpChannel ? (
            <Typography variant="body2">
              <Trans>
                If you have questions, contact us on Slack via #{orgHelpChannel}
                , or visit the{' '}
                <Link href={routeForSupport()}>Support Center</Link> to open a
                support case.
              </Trans>
            </Typography>
          ) : (
            <Typography variant="body2">
              <Trans>
                If you have questions, please visit the{' '}
                <Link href={routeForSupport()}>Support Center</Link> to open a
                support case.
              </Trans>
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  } else {
    return null;
  }
}
